import { memo } from 'react';
import { Flex } from '@optiaxiom/react';
import { NodeProps } from '@xyflow/react';

import { MatchType } from '@frontend/types/workflow-agent';

import { useNodeRenderer } from '../../../../hooks/useNodeRenderer';
import { NodeContainer } from '../../container/NodeContainer';
import { NodeDescription } from '../../node-parts/NodeDescription';
import { NodeHeader } from '../../node-parts/NodeHeader';
import { nodeContent } from '../../shared/shared.css';

import { conditionNodeContainer } from './ConditionNodeRenderer.css';

export type ConditionNodeRendererProps = {
  data: {
    type: string;
    name?: string;
    matching_condition?: string;
    match_type?: MatchType;
    [key: string]: unknown;
  };
} & NodeProps;

// Helper function to convert MatchType enum to human-readable text
function getMatchTypeLabel(matchType: MatchType): string {
  switch (matchType) {
    case MatchType.EQUALS:
      return 'value equals';
    case MatchType.NOT_EQUALS:
      return 'value not equals';
    case MatchType.CONTAINS:
      return 'value contains';
    case MatchType.NOT_CONTAINS:
      return 'value not contains';
    case MatchType.GREATER_THAN:
      return 'value >';
    case MatchType.LESS_THAN:
      return 'value <';
    case MatchType.GREATER_THAN_OR_EQUALS:
      return 'value >=';
    case MatchType.LESS_THAN_OR_EQUALS:
      return 'value <=';
    case MatchType.REGEX:
      return 'value matches regex';
    default:
      return 'value equals';
  }
}

export const ConditionNodeRenderer = memo(({ data, type, selected, id }: NodeProps) => {
  const dataObj = data as Record<string, unknown>;
  const {
    iconInfo,
    hasErrors,
    handleMouseEnter,
    handleMouseLeave,
    nodeTypeName,
    nodeType,
    isSourceNode,
  } = useNodeRenderer({ id, type, data: dataObj, selected });

  if (!nodeType) {
    console.warn(`Node type '${nodeTypeName}' not found in registry`);
    return <div>Unknown node type: {nodeTypeName}</div>;
  }

  const label = 'Condition';
  const matchingCondition =
    typeof dataObj?.matching_condition === 'string' ? dataObj.matching_condition : '';
  const matchType = (dataObj?.match_type as MatchType) || MatchType.EQUALS;

  const conditionDisplay = matchingCondition
    ? `If ${getMatchTypeLabel(matchType)} "${matchingCondition}"`
    : 'No condition set';

  return (
    <NodeContainer
      selected={selected}
      hasErrors={hasErrors}
      isSourceNode={isSourceNode}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={conditionNodeContainer}
      nodeId={id}
    >
      <Flex className={nodeContent}>
        <NodeHeader iconInfo={iconInfo} label={label} nodeType={nodeTypeName} variant="condition" />
        <NodeDescription description={conditionDisplay} lineClamp="1" />
      </Flex>
    </NodeContainer>
  );
});

ConditionNodeRenderer.displayName = 'ConditionNodeRenderer';
